import React from 'react';

import Typography from '@mui/material/Typography';

import HandsonArticleLayout from './components/HandsonArticleLayout';
import WorkflowChecklist from './components/WorkflowChecklist';
import WorkflowCodeBlock from './components/WorkflowCodeBlock';
import WorkflowDirectoryTree from './components/WorkflowDirectoryTree';
import WorkflowGoalBox from './components/WorkflowGoalBox';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowLinkCard from './components/WorkflowLinkCard';
import WorkflowList from './components/WorkflowList';
import WorkflowLockedCodeBlock from './components/WorkflowLockedCodeBlock';
import WorkflowSection from './components/WorkflowSection';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';

const seriesSteps = [
  {
    label: 'Steeringルール',
    title: 'Steeringルールの書き方を学ぶ',
    path: '/devdocs/handson/steering-rules',
  },
  { label: 'rfp作成', title: 'rfpを作成する', active: true },
  {
    label: 'proposal/business',
    title: 'proposal/businessを生成する',
    path: '/devdocs/handson/proposal-business',
  },
  { label: 'usecase', title: 'usecaseを生成する', path: '/devdocs/handson/usecase' },
  { label: 'product', title: 'productの機能一覧を生成する', path: '/devdocs/handson/product' },
];

const HandsonRfp: React.FC = () => {
  return (
    <HandsonArticleLayout
      title="要件定義編 — rfp作成"
      seriesName="要件定義編"
      tags={['Hands-on', 'RFP', 'Requirements']}
      seriesSteps={seriesSteps}
      prevLink={{
        label: 'Steeringルールの書き方',
        path: '/devdocs/handson/steering-rules',
      }}
      nextLink={{
        label: 'proposal/business生成',
        path: '/devdocs/handson/proposal-business',
      }}
    >
      <WorkflowGoalBox
        prerequisites={[
          '「要件定義編 — Steeringルールの書き方」が完了している',
          '「導入編 — ストーリー導入 + プロジェクト雛形作成」の導入ストーリーの内容を覚えている（忘れていたら読み返す）',
        ]}
        completionCriteria={[
          '.kiro/requirements/todo/todo.rfp.md が作成されている',
          'RFPの基本構造（ゴール・進め方・機能情報）が記載されている',
        ]}
      >
        Aの相談内容をもとに、AIとの対話のスタートラインとなる rfp.md を作成する。
      </WorkflowGoalBox>

      <WorkflowLinkCard
        title="要件定義編 — Steeringルールの書き方"
        description="前の記事"
        path="/devdocs/handson/steering-rules"
      />

      <WorkflowLinkCard
        title="導入編 — ストーリー導入 + プロジェクト雛形作成"
        description="導入ストーリー"
        path="/devdocs/handson/story-scaffold"
      />

      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: 'RFPの書き方を確認する' },
          { id: 'section-2', label: 'rfp.mdを作成する' },
          { id: 'section-3', label: 'rfp.md - 完成例' },
          { id: 'section-4', label: '現在のプロジェクト構成' },
        ]}
      />

      {/* セクション1 */}
      <WorkflowSection title="RFPの書き方を確認する" id="section-1" number={1}>
        <WorkflowText>
          以下の記事を読んで、RFPの構造・書くべき内容・ベストプラクティスを把握してください。
        </WorkflowText>

        <WorkflowLinkCard
          title="AIと作る、壊れないバックエンド紹介記事 — RFPを作成する"
          description="RFPの基本構造・テンプレート・記載例・ベストプラクティス/アンチパターン"
          path="/devdocs/workflow/req-step1"
        />

        <WorkflowInfoBox type="info">
          <Typography variant="body2" fontWeight={600}>
            このハンズオンでは「ふわっとしている場合」で書いてください。
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            「この程度なら自分で全部決められる」という人もいるかもしれませんが、ここでの目的は
            AIとの対話で仕様を詰めていくプロセスを体験する
            ことです。自分で全て決めてしまうとAIを使う意味が薄れます。あえて詳細を書かずに余白を残し、次のステップでAIに深掘りしてもらう——その流れを体験してください。
          </Typography>
        </WorkflowInfoBox>
      </WorkflowSection>

      {/* セクション2 */}
      <WorkflowSection title="rfp.mdを作成する" id="section-2" number={2}>
        <WorkflowSection title="ファイルを作成する" subNumber="2.1">
          <WorkflowText>以下のパスにファイルを作成してください。</WorkflowText>
          <WorkflowCodeBlock language="text" code=".kiro/requirements/todo/todo.rfp.md" />
          <WorkflowCodeBlock language="bash" code="mkdir -p .kiro/requirements/todo" />
        </WorkflowSection>

        <WorkflowSection title="書いてみる" subNumber="2.2">
          <WorkflowText>
            記事1の導入ストーリーのAの相談内容と、上で読んだRFPガイドを参考にして、自分の言葉で
            rfp.md を書いてください。
          </WorkflowText>

          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>注意</strong>:
              Aの困りごとを「日常の言葉」のまま貼り付けるのではなく、エンジニアとして「システム要件」に翻訳して書くことがポイントです。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション3 */}
      <WorkflowSection title="rfp.md - 完成例" id="section-3" number={3}>
        <WorkflowLockedCodeBlock password="Kp7mXw">
          {`# todo 機能の要件草案

## ゴール

- 対話を行いながら todo 機能の草案をまとめる。
- 決定した内容をもとに \`todo.proposal.md\` を作成する。
- 決定した内容をもとに \`todo.business.md\` を作成する。

## 進め方

- あなた が 私 に質問をして、それに回答する形でゴールを目指す
- 私 の回答に矛盾や設計上の問題がある場合は、遠慮せず指摘すること。Yesマンにはならない。
- 代替案がある場合はメリット・デメリットと、あなたはどちらが良いと思うかも添えて提示すること。
- わからないことは推測せずに聞くこと。
- 決定が曖昧なまま先に進まないこと。
- 開発期間は1〜2週間を想定している。スコープを広げすぎず、短期で実現可能な範囲に絞って提案すること。

## やりたいこと

- チームのタスク管理をWebアプリケーションで行えるようにしたい
- 「誰が何をいつまでにやるか」「終わったかどうか」を一覧で見えるようにする
- 期限を過ぎたタスクが一目で分かるようにしたい

## 誰が使うか

- チームリーダー（タスクの作成・割り当て・全体把握）
- チームメンバー（自分のタスク確認・完了報告）

## 扱うデータ

- TODO（タイトル、担当者、期限、完了状態）
- 他に必要そうな情報があれば提案して

## 前提・制約

- 認証機能なし（ログインなしで利用する）
- シンプルさ重視（最小限の機能で動くものを短期で作る）
- 同時利用は5〜10人程度のチーム規模を想定

## 分からないこと・決めたいこと

- TODOのステータスはどんな種類が必要か（未着手/進行中/完了？）
- 担当者の管理方法（自由入力テキスト？ メンバーリストから選択？）
- 優先度は必要か
- TODOの編集・削除は誰ができるか
- 一覧の表示順はどうするか（期限が近い順？ 作成順？）

他に必要そうな項目や、あった方が良い機能があれば提案して`}
        </WorkflowLockedCodeBlock>
      </WorkflowSection>

      {/* セクション4 */}
      <WorkflowSection title="現在のプロジェクト構成" id="section-4" number={4}>
        <WorkflowDirectoryTree
          root="team-todo/"
          entries={[
            {
              name: '.kiro',
              color: '#1565c0',
              children: [
                {
                  name: 'requirements',
                  children: [
                    {
                      name: 'todo',
                      children: [{ name: 'todo.rfp.md', desc: '今回作成', highlight: '#e65100' }],
                    },
                  ],
                },
                {
                  name: 'steering',
                  children: [{ name: 'product.md' }],
                },
              ],
            },
            { name: 'build.gradle' },
            { name: 'settings.gradle' },
            { name: 'docker-compose.yml' },
            { name: 'HELPME.md' },
            {
              name: 'src',
              children: [
                {
                  name: 'main',
                  children: [
                    {
                      name: 'java/com/example/teamtodo',
                      children: [
                        { name: 'TeamTodoApplication.java' },
                        { name: 'config', children: [{ name: 'SecurityConfig.java' }] },
                      ],
                    },
                    { name: 'resources', children: [{ name: 'application.yml' }] },
                  ],
                },
                { name: 'test', children: [] },
              ],
            },
          ]}
        />
      </WorkflowSection>

      {/* 次のステップ */}
      <WorkflowInfoBox type="preview">
        <Typography variant="body2" fontWeight={600}>
          次の記事
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          要件定義編 — proposal/business生成
        </Typography>
        <WorkflowList
          items={[
            'proposal/business生成用のSteeringルールを作成する',
            'rfp.mdを入力としてAIと対話する',
            '生成結果をチェックリストで品質確認する',
          ]}
        />
      </WorkflowInfoBox>

      <WorkflowLinkCard
        title="要件定義編 — proposal/business生成"
        description="proposal/business生成用のSteeringルールを作成し、rfp.mdを入力としてAIと対話する"
        path="/devdocs/handson/proposal-business"
      />

      {/* チェックリスト */}
      <WorkflowChecklist
        groups={[
          {
            title: 'チェックリスト',
            items: [
              '.kiro/requirements/todo/todo.rfp.md が作成されている',
              'RFPに「ゴール」「進め方」が記載されている',
              'RFPに「やりたいこと」「誰が使うか」「扱うデータ」が記載されている',
              'RFPに「前提・制約」「分からないこと・決めたいこと」が記載されている',
            ],
          },
        ]}
      />
    </HandsonArticleLayout>
  );
};

export default HandsonRfp;
